import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Driver } from 'driver.js'
import { RootState } from 'store/modules/rootReducer'
import { markRestartCycleTourSeen } from 'store/modules/tour'
import { getRestartCycleTourConfig, createDriverInstance } from '../utils/tourConfig'
import { trackRestartCycleTourShown } from '../utils/tourTracking'
import { useTracking } from 'react-tracking'

interface UseRestartCycleTourProps {
  isDarkMode: boolean
}

export const useRestartCycleTour = ({ isDarkMode }: UseRestartCycleTourProps) => {
  const dispatch = useDispatch()
  const { trackEvent } = useTracking()
  const driverRef = useRef<Driver | null>(null)
  const tourState = useSelector((state: RootState) => state.tour.dashboard)

  // Check if element exists
  const elementExists = useCallback((selector: string): boolean => {
    try {
      const element = document.querySelector(selector)
      return element !== null && element instanceof HTMLElement
    } catch {
      return false
    }
  }, [])

  // Start restart cycle tour
  const startTour = useCallback(() => {
    // Don't show if already seen
    if (tourState.restartCycleTourSeen) return

    // Check if restart button exists
    if (!elementExists('[data-tour="dashboard-restart-cycle"]')) return

    // Get tour config
    const config = getRestartCycleTourConfig(isDarkMode)

    // Verify element exists
    if (!elementExists(config[0].element)) return

    // Track tour shown
    trackEvent(trackRestartCycleTourShown())

    // Create driver instance with onDestroyed callback
    const handleTourComplete = () => {
      // Mark as seen when tour completes
      dispatch(markRestartCycleTourSeen())
    }

    const driverInstance = createDriverInstance(isDarkMode, handleTourComplete)

    // Configure driver with steps
    driverInstance.setSteps(
      config.map((stepConfig) => ({
        element: stepConfig.element,
        popover: {
          title: stepConfig.popover.title,
          description: stepConfig.popover.description,
          side: stepConfig.popover.side || 'top',
          align: stepConfig.popover.align || 'center',
        },
      }))
    )

    // Store the instance
    driverRef.current = driverInstance

    // Start tour with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (driverRef.current) {
        try {
          driverRef.current.drive()
        } catch (error) {
          console.error('Error starting restart cycle tour:', error)
          // Mark as seen on error to prevent retry loops
          dispatch(markRestartCycleTourSeen())
        }
      }
    }, 500)
  }, [isDarkMode, tourState.restartCycleTourSeen, dispatch, elementExists, trackEvent])

  // Auto-start tour when button appears and hasn't been seen
  useEffect(() => {
    if (!tourState.restartCycleTourSeen && elementExists('[data-tour="dashboard-restart-cycle"]')) {
      const timer = setTimeout(() => {
        startTour()
      }, 1000) // Delay to ensure everything is loaded

      return () => clearTimeout(timer)
    }
  }, [tourState.restartCycleTourSeen, startTour, elementExists])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return {
    startTour,
  }
}
